/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/templates/QaTemplateContentsPane.tsx
 * FILE_ID: QA-TEMPLATES-CONTENTS-001
 * PURPOSE: Editable checklist and step selection for a published template.
 * LANDMINE: Template heading is read-only. No HOLD chip. Titles stay in picker state.
 * Several checklists may be open at once; an open header sticks to the list top.
 */
import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { InlineRenameInput } from '../../../qa/InlineRenameInput'
import { joinProductNames } from '../../../qa/qaProductResolve'
import type { QaTemplatePicker } from './useQaTemplatePicker'

export function QaTemplateContentsPane({
  picker,
  collapsible = false,
}: {
  picker: QaTemplatePicker
  collapsible?: boolean
}) {
  const [editingSection, setEditingSection] = useState<string | null>(null)
  const [editingItem, setEditingItem] = useState<string | null>(null)
  const [paneOpen, setPaneOpen] = useState(false)
  const detail = picker.detail
  if (!detail) {
    return <p className="text-sm text-muted-foreground">Choose a system to see its checklists.</p>
  }
  if (detail.sections.length === 0) {
    return <p className="text-sm text-muted-foreground">This template has no checklists yet.</p>
  }
  const shown = !collapsible || paneOpen
  const allSectionsOpen = detail.sections.every((section) => picker.expandedSectionIds.has(section.id))
  const setAllSections = () => {
    for (const section of detail.sections) {
      const open = picker.expandedSectionIds.has(section.id)
      if (allSectionsOpen ? open : !open) picker.toggleExpandedSection(section.id)
    }
  }
  return (
    <div className="flex min-h-0 flex-col">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          {collapsible ? (
            <button
              type="button"
              aria-expanded={paneOpen}
              onClick={() => setPaneOpen((value) => !value)}
              className="flex min-h-[44px] w-full items-center gap-2 text-left text-sm font-semibold"
            >
              {paneOpen ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
              <span className="truncate">{detail.name}</span>
            </button>
          ) : (
            <p className="truncate text-sm font-semibold">{detail.name}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => (picker.counts.checklists > 0 ? picker.clearAll() : picker.selectAll())}
          className="shrink-0 text-xs font-medium underline"
        >
          {picker.counts.checklists > 0 ? 'Clear all' : 'Select all'}
        </button>
        {collapsible ? (
          <button
            type="button"
            onClick={setAllSections}
            className="shrink-0 text-xs font-medium underline"
          >
            {allSectionsOpen ? 'Collapse all' : 'Expand all'}
          </button>
        ) : null}
      </div>
      {shown ? <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
        {detail.sections.map((section) => {
          const expanded = picker.expandedSectionIds.has(section.id)
          const selected = picker.selectedSectionIds.has(section.id)
          const itemSet = picker.selectedItemIds[section.id] ?? new Set<string>()
          const title = picker.sectionTitleOverrides[section.id] ?? section.title
          const sectionNames = picker.productDraft.sectionNames[section.id] ?? []
          return (
            <div key={section.id} className="rounded-md border border-border">
              <div
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                data-testid={`qa-template-section-${section.id}`}
                onClick={() => {
                  picker.setProductFocus({ kind: 'checklist', sectionId: section.id })
                  picker.toggleExpandedSection(section.id)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    picker.setProductFocus({ kind: 'checklist', sectionId: section.id })
                    picker.toggleExpandedSection(section.id)
                  }
                }}
                className={`flex min-h-[44px] min-w-0 items-center gap-2 px-2${
                  expanded ? ' sticky top-0 z-[1] rounded-t-md bg-card' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => picker.toggleSection(section.id)}
                  className="h-4 w-4 rounded border-border"
                />
                <div className="min-w-0 flex-1">
                  {editingSection === section.id ? (
                    <InlineRenameInput
                      initialValue={title}
                      ariaLabel="Checklist name"
                      onSubmit={(value) => {
                        picker.setSectionTitle(section.id, value)
                        setEditingSection(null)
                      }}
                      onCancel={() => setEditingSection(null)}
                      className="flex-1"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        picker.setProductFocus({ kind: 'checklist', sectionId: section.id })
                        setEditingSection(section.id)
                      }}
                      className="min-w-0 w-full truncate text-left text-sm font-medium"
                    >
                      {title}{section.is_optional ? ' - optional' : ''}
                    </button>
                  )}
                  {sectionNames.length > 0 ? (
                    <p className="truncate text-xs text-muted-foreground">{joinProductNames(sectionNames)}</p>
                  ) : null}
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">{itemSet.size}/{section.items.length}</span>
                {expanded
                  ? <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  : <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
              </div>
              {expanded ? (
                <div className="space-y-2 border-t border-border px-2 py-2">
                  {section.items.map((item) => {
                    const itemTitle = picker.itemTitleOverrides[item.id] ?? item.title
                    const itemNames = picker.productDraft.itemNames[item.id] ?? []
                    return (
                      <div
                        key={item.id}
                        className="flex min-h-[44px] min-w-0 items-center gap-2"
                        onClick={() => picker.setProductFocus({
                          kind: 'step',
                          sectionId: section.id,
                          itemId: item.id,
                        })}
                      >
                        <input
                          type="checkbox"
                          checked={itemSet.has(item.id)}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => picker.toggleItem(section.id, item.id)}
                          className="h-4 w-4 rounded border-border"
                        />
                        <span className="shrink-0 text-xs text-muted-foreground">{item.item_number}</span>
                        <div className="min-w-0 flex-1">
                          {editingItem === item.id ? (
                            <InlineRenameInput
                              initialValue={itemTitle}
                              ariaLabel="Step text"
                              onSubmit={(value) => {
                                picker.setItemTitle(item.id, value)
                                setEditingItem(null)
                              }}
                              onCancel={() => setEditingItem(null)}
                              className="flex-1"
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => setEditingItem(item.id)}
                              className="min-w-0 w-full truncate text-left text-sm"
                            >
                              {itemTitle}
                            </button>
                          )}
                          {itemNames.length > 0 ? (
                            <p className="truncate text-xs text-muted-foreground">{joinProductNames(itemNames)}</p>
                          ) : null}
                        </div>
                      </div>
                    )
                  })}
                  <p className="text-xs text-muted-foreground">+ Add step after insert from Build.</p>
                </div>
              ) : null}
            </div>
          )
        })}
      </div> : null}
    </div>
  )
}
